import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config';
import { useDomainValidation } from '../../hooks/useDomainValidation';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';
import DomainValidationModal from '../DomainValidationRetryModal';

function ObjectiveForm({ onClose, onSave, fetchWithAuth, editingObjective }) {
  const { domainValidation, handleValidationResponse, clearValidation } = useDomainValidation();
  // Escape dismisses this modal — suppressed while the domain-validation modal is stacked on top,
  // so Escape closes that one first rather than tearing down the form underneath it.
  useEscapeToClose(onClose, !domainValidation);
  const [formData, setFormData] = useState({
    objective_type: 'weight_loss',
    title: '',
    description: '',
    target_value: '',
    current_value: '',
    unit: '',
    priority: 3,
    target_date: '',
    is_active: true
  });

  useEffect(() => {
    if (editingObjective) {
      setFormData({
        objective_type: editingObjective.objective_type || 'weight_loss',
        title: editingObjective.title || '',
        description: editingObjective.description || '',
        target_value: editingObjective.target_value || '',
        current_value: editingObjective.current_value || '',
        unit: editingObjective.unit || '',
        priority: editingObjective.priority || 3,
        target_date: editingObjective.target_date ?
        new Date(editingObjective.target_date).toISOString().split('T')[0] : '',
        is_active: editingObjective.is_active !== undefined ? editingObjective.is_active : true
      });
    }
  }, [editingObjective]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      target_value: formData.target_value ? parseFloat(formData.target_value) : null,
      current_value: formData.current_value ? parseFloat(formData.current_value) : null,
      priority: parseInt(formData.priority),
      target_date: formData.target_date === '' ? null : formData.target_date
    };

    try {
      const url = editingObjective
        ? `${API_BASE}/api/health/objectives/${editingObjective.id}`
        : `${API_BASE}/api/health/objectives`;

      const method = editingObjective ? 'PUT' : 'POST';

      const response = await fetchWithAuth(url, {
        method: method,
        body: JSON.stringify(submitData)
      });

      const validationResult = await handleValidationResponse(response, 'health objective');
      if (validationResult === true) {
        onSave();
        onClose();
      }
    } catch (error) {
      console.error('Failed to save objective:', error);
    }
  };

  return (
    <>{domainValidation && (
      <DomainValidationModal
        isOpen={true}
        mode={domainValidation.mode}
        rejectionMessage={domainValidation.message}
        entityType={domainValidation.entityType}
        onClose={clearValidation}
      />
    )}<div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{editingObjective ? 'Edit Health Objective' : 'Add Health Objective'}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Objective Type</label>
            <select
              className="form-select"
              value={formData.objective_type}
              onChange={(e) => setFormData(prev => ({...prev, objective_type: e.target.value}))}
            >
              <option value="weight_loss">Weight Loss</option>
              <option value="weight_gain">Weight Gain</option>
              <option value="nutrition">Nutrition Goal</option>
              <option value="exercise">Exercise Goal</option>
              <option value="health_metric">Health Metric</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Title *</label>
            <input
              type="text"
              className="form-input"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
              placeholder="e.g. Lose 20 pounds, Lower blood pressure"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
              placeholder="Detailed description of your health objective"
              rows="3"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Current Value</label>
              <input
                type="number"
                step="0.1"
                className="form-input"
                value={formData.current_value}
                onChange={(e) => setFormData(prev => ({...prev, current_value: e.target.value}))}
                placeholder="e.g. 180"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Target Value</label>
              <input
                type="number"
                step="0.1"
                className="form-input"
                value={formData.target_value}
                onChange={(e) => setFormData(prev => ({...prev, target_value: e.target.value}))}
                placeholder="e.g. 160"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Unit</label>
              <input
                type="text"
                className="form-input"
                value={formData.unit}
                onChange={(e) => setFormData(prev => ({...prev, unit: e.target.value}))}
                placeholder="e.g. lbs, mg/dL"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Priority</label>
              <select
                className="form-select"
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({...prev, priority: e.target.value}))}
              >
                <option value={1}>1 - Low</option>
                <option value={2}>2</option>
                <option value={3}>3 - Medium</option>
                <option value={4}>4</option>
                <option value={5}>5 - High</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Target Date</label>
              <input
                type="date"
                className="form-input"
                value={formData.target_date}
                onChange={(e) => setFormData(prev => ({...prev, target_date: e.target.value}))}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({...prev, is_active: e.target.checked}))}
              />
              Active Objective
            </label>
          </div>

          <div className="flex gap-2">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {editingObjective ? 'Update Objective' : 'Save Objective'}
            </button>
          </div>
        </form>
      </div>
    </div></>
  );
}

export default ObjectiveForm;
